// apps/web/src/components/DataReconfirmation/DataReconfirmationProvider.tsx
//
// Identity-reconfirmation gate. Mirrors TourProvider: reads a field off the
// authenticated user (here `requiresDataConfirmation`, from GET /auth/me) and
// drives an effect/overlay off it. When true, opens the blocking
// DataReconfirmationModal. Confirming stamps POST /profile/confirm-data, then
// refreshUser() re-reads /auth/me so requiresDataConfirmation flips to false.
//
// Priority: this mounts inside AppLayout AFTER the missing-consent redirect, so
// the consent gate (route-level) always wins. It also stands down while a legal
// re-acceptance is pending so two blocking overlays never stack — reacceptance
// first, reconfirmation after. Contract: docs/contracts/ESCR-identity-reconfirmation.md.

import { useState } from 'react';

import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useConfirmData } from '@alamo/api';
import { useAuth } from '@alamo/auth';

import { DataReconfirmationModal } from './DataReconfirmationModal';

export function DataReconfirmationProvider(): JSX.Element {
  const { t } = useTranslation(['auth', 'errors']);
  const { user, refreshUser } = useAuth();
  const confirm = useConfirmData();

  // Local dismiss — closes the modal immediately on a successful confirm even
  // before refreshUser()'s /auth/me round-trip lands (avoids a flash of the
  // still-open modal). The server field is the source of truth on next mount.
  const [dismissedLocally, setDismissedLocally] = useState(false);

  const open =
    Boolean(user?.requiresDataConfirmation) &&
    // Let a pending legal re-acceptance resolve first (single blocking overlay).
    !user?.pending_reacceptance &&
    !dismissedLocally;

  const handleConfirm = async (): Promise<void> => {
    try {
      await confirm.mutateAsync();
      await refreshUser();
      setDismissedLocally(true);
    } catch {
      toast.error(
        t('auth:profile.reconfirmation.error', {
          defaultValue: t('errors:generic', {
            defaultValue: 'Não foi possível confirmar seus dados. Tente novamente.',
          }),
        }),
      );
    }
  };

  return (
    <DataReconfirmationModal
      open={open}
      onConfirm={() => void handleConfirm()}
      isConfirming={confirm.isPending}
    />
  );
}
